import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import '../components/aiinterview/aiinterview.css';
import { aiInterviewsAPI } from '../lib/aiInterviewApi';
import { Loading } from '../components/aiinterview/recruiter/common';
import ReportHeader from '../components/aiinterview/report/ReportHeader';
import { AdvisoryBanner, DecisionBar } from '../components/aiinterview/report/AdvisoryDecision';
import CompetencyScores from '../components/aiinterview/report/CompetencyScores';
import { ClaimMatrix, InsightsPanel } from '../components/aiinterview/report/ClaimsInsights';
import RecordingTranscript from '../components/aiinterview/report/RecordingTranscript';
import IntegrityPanel from '../components/aiinterview/report/IntegrityPanel';
import { BlueprintPanel, DetailsPanel, PendingPanel } from '../components/aiinterview/report/DetailsPanels';

const LIVE = ['starting', 'in_progress', 'completed', 'scoring'];

export default function AIInterviewReportPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [d, setD] = useState(null);
  const [turns, setTurns] = useState([]);
  const [signals, setSignals] = useState([]);
  const [missing, setMissing] = useState(false);
  const seekRef = useRef(() => {});
  const scrolled = useRef(false);

  const load = useCallback(async () => {
    try {
      const [a, b, c] = await Promise.all([aiInterviewsAPI.get(id), aiInterviewsAPI.transcript(id), aiInterviewsAPI.signals(id)]);
      setD(a.data);
      setTurns(b.data);
      setSignals(c.data);
      return a.data;
    } catch (e) {
      if (e.response?.status === 404) setMissing(true);
      else toast.error('Could not load the interview');
      return null;
    }
  }, [id]);

  useEffect(() => {
    let t;
    const poll = () => load().then((doc) => { if (doc && (LIVE.includes(doc.status) || doc.blueprint_status === 'generating' || doc.blueprint_status === 'pending')) t = setTimeout(poll, 6000); });
    poll();
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    if (d && params.get('focus') === 'integrity' && !scrolled.current) {
      scrolled.current = true;
      setTimeout(() => document.getElementById('integrity')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  }, [d, params]);

  const seek = (target) => seekRef.current(target);
  if (missing) return <div className="iv iv-rpage"><div className="iv-card" data-testid="report-not-found">Interview not found.</div></div>;
  if (!d) return <div className="iv iv-rpage"><Loading /></div>;

  return (
    <div className="iv iv-rpage" data-testid="ai-interview-report-page">
      <ReportHeader d={d} reload={load} />
      {d.report ? (
        <>
          <AdvisoryBanner report={d.report} />
          <DecisionBar d={d} onDone={load} />
        </>
      ) : <PendingPanel d={d} />}
      <div className="iv-grid2">
        <div>
          {d.report && <CompetencyScores d={d} onSeek={seek} reload={load} />}
          {d.report && <ClaimMatrix report={d.report} onSeek={seek} />}
          {d.report && <InsightsPanel report={d.report} />}
          {!d.report && <BlueprintPanel d={d} reload={load} />}
        </div>
        <div>
          {(turns.length > 0 || d.report) && <RecordingTranscript id={id} turns={turns} seekRef={seekRef} />}
          <IntegrityPanel d={d} signals={signals} onSeek={seek} reload={load} />
        </div>
      </div>
      <DetailsPanel d={d} reload={load} />
      {d.report && <BlueprintPanel d={d} reload={load} />}
    </div>
  );
}
